import { el } from './dom';

export interface LoginViewProps {
  pending: boolean;
  error: string | null;
  onSubmit: (username: string, password: string) => void;
}

/**
 * The one screen that is built once and updated, rather than rebuilt.
 *
 * Everywhere else a re-render throws the old nodes away, which is fine for content the
 * app owns. These two inputs are not that: they hold what the user typed, they hold the
 * focus, and a password manager's inline autofill menu is anchored to them. Replacing
 * the input under an open Bitwarden menu leaves the menu on screen with nothing behind
 * it - it never gets the blur it closes on. So `update` restates pending and error, and
 * the fields themselves stay put for the life of the screen.
 */
export interface LoginView {
  node: HTMLElement;
  update: (state: { pending: boolean; error: string | null }) => void;
  /**
   * Called when the screen goes away for good. Blurs first, so an extension anchored to
   * a field is told the field is gone, and empties both inputs so a password isn't left
   * sitting in a detached node for the rest of the session.
   */
  destroy: () => void;
}

export function loginView(props: LoginViewProps): LoginView {
  let pending = props.pending;

  const username = el('input', {
    id: 'username',
    name: 'username',
    type: 'text',
    autocomplete: 'username',
    autocapitalize: 'none',
    autocorrect: 'off',
    required: true,
  });

  const password = el('input', {
    id: 'password',
    name: 'password',
    type: 'password',
    autocomplete: 'current-password',
    required: true,
  });

  // Always present, empty when there is nothing to say - `.error:empty` hides it. A
  // conditional node would mean rebuilding the form to show a failed sign-in.
  const errorNode = el('p', { class: 'error' });
  const submit = el('button', { class: 'btn btn--primary', type: 'submit' });

  const form = el(
    'form',
    {
      class: 'panel stack',
      onsubmit: (event: Event) => {
        event.preventDefault();
        if (pending) return;
        props.onSubmit(username.value.trim(), password.value);
      },
    },
    el('p', { class: 'muted' }, 'This one is invite-only. Sign in to keep looking.'),
    el('div', { class: 'field' }, el('label', { for: 'username' }, 'Username'), username),
    el('div', { class: 'field' }, el('label', { for: 'password' }, 'Password'), password),
    errorNode,
    submit,
  );

  const node = el('div', { class: 'grow' }, form);

  const update = (state: { pending: boolean; error: string | null }): void => {
    pending = state.pending;
    submit.disabled = state.pending;
    submit.textContent = state.pending ? 'Signing in…' : 'Sign in';
    errorNode.textContent = state.error ?? '';
  };

  update(props);
  // Once, on the way in: re-focusing on every update would fight both the user and an
  // autofill menu anchored to the other field.
  queueMicrotask(() => username.focus());

  return {
    node,
    update,
    destroy: () => {
      if (node.contains(document.activeElement)) {
        (document.activeElement as HTMLElement).blur();
      }
      username.value = '';
      password.value = '';
    },
  };
}
